import { formatName, useCreateSocialOffer } from "@api";
import type {
  ILlmExtractSocialOffer,
  ISocialOfferWithFavoriteChallenges,
  TLlmExtractOfferContact,
  TSocialOfferCreationOrEdition,
} from "@api";
import type { ISettingValueMaster } from "@bbj-types/setting";
import type { TStructurePartnerOption } from "@inputs/autocomplete/AutocompleteStructurePartners";
import { InternalOfferSource } from "@root/utils/enums/offer";
import type { FieldValues } from "react-hook-form";
import type { TSocialOfferForm } from ".";
import { formatContactFromExtractedOffer } from "../logics";

type TSettingOption = Pick<ISettingValueMaster, "label" | "value" | "group">;

export interface ISocialOfferSettingOptions {
  tags: TSettingOption[];
  financingModes: TSettingOption[];
  defaultProvider?: TStructurePartnerOption;
  phoneCode: string;
}

// Maps raw setting values from the AI to the autocomplete options, dropping unknown values
const toSettingOptions = (values: string[] | undefined, options: TSettingOption[]) =>
  (values ?? []).flatMap((value) => {
    const option = options.find((opt) => opt.value === value);
    return option ? [{ label: option.label, value: option.value, ...(option.group && { group: option.group }) }] : [];
  });

const formatSocialOfferContactFromAI = (
  contact: TLlmExtractOfferContact,
  phoneCode: string
): FieldValues | undefined => {
  const formatted = formatContactFromExtractedOffer(contact, phoneCode);
  if (!formatted?.email && !formatted?.phone) {
    return undefined;
  }
  const { firstName, lastName, job, email, phone } = formatted;
  return { firstName, lastName, job, email, phone };
};

export const parseSocialOfferFromAI =
  (options: ISocialOfferSettingOptions) =>
  (item: ILlmExtractSocialOffer): FieldValues => ({
    name: item.name,
    description: item.description,
    beneficiaries: item.beneficiaries,
    registrationModalities: item.registrationModalities,
    url: item.url,
    isRemote: item.isRemote ?? false,
    permanent: item.serviceStartDate || item.serviceEndDate ? "temporary" : "permanent",
    ...(item.serviceStartDate || item.serviceEndDate
      ? { serviceDates: { startDate: item.serviceStartDate, endDate: item.serviceEndDate } }
      : {}),
    tags: toSettingOptions(item.tags, options.tags),
    financingModes: toSettingOptions(item.financingModes, options.financingModes),
    ...(item.address ? { address: item.address } : {}),
    ...(item.perimeter?.length
      ? { perimeter: item.perimeter.map((place) => ({ id: place._id, label: place.placeLabel })) }
      : {}),
    ...(item.provider?.name
      ? {
          provider: item.provider._id
            ? { _id: item.provider._id, name: item.provider.name }
            : { name: item.provider.name },
        }
      : options.defaultProvider
        ? { provider: options.defaultProvider }
        : {}),
    createStructurePartner: true,
    ...(item.contact ? { contact: formatSocialOfferContactFromAI(item.contact, options.phoneCode) } : {}),
  });

export const formatSocialOfferFromAI = (item: TSocialOfferForm): TSocialOfferCreationOrEdition => ({
  source: InternalOfferSource.BBJ_ASSISTANT,
  name: item.name,
  ...(item.permanent === "temporary" &&
    item.serviceDates?.startDate && { serviceStartDate: new Date(item.serviceDates.startDate) }),
  ...(item.permanent === "temporary" &&
    item.serviceDates?.endDate && { serviceEndDate: new Date(item.serviceDates.endDate) }),
  tags: item.tags?.map((tag) => tag.value) ?? [],
  financingModes: item.financingModes?.map((mode) => mode.value) ?? [],
  description: item.description,
  beneficiaries: item.beneficiaries,
  registrationModalities: item.registrationModalities,
  url: item.url,
  address: item.address,
  loc: item.address?.loc,
  provider: { name: item.provider.name, providerId: item.provider._id },
  createStructurePartner: !item.provider._id && item.createStructurePartner,
  perimeter: item.perimeter?.map((place) => place.id),
  ...(item.contact && {
    contact: {
      name: formatName(item.contact),
      comment: item.contact.job ?? "",
      mail: item.contact.email ?? "",
      phone: item.contact.phone ?? "",
    },
  }),
  isRemote: item.isRemote,
});

export const useCreateSocialOffers = () => {
  const [{ loading }, createSocialOffer] = useCreateSocialOffer();

  const createSocialOffers = async (items: TSocialOfferForm[]) => {
    const created: ISocialOfferWithFavoriteChallenges[] = [];
    const failed: TSocialOfferForm[] = [];
    const failedIndexes: number[] = [];
    const createdProviderIds = new Set<string>();
    for (const [index, item] of items.entries()) {
      try {
        const payload = formatSocialOfferFromAI(item);
        const result = await createSocialOffer(payload);
        if (result.status === 201 && result.data) {
          created.push({ ...result.data, favoriteChallenges: [] });
          const providerId = result.data.provider?.providerId;
          if (payload.createStructurePartner && providerId) {
            createdProviderIds.add(String(providerId));
          }
        } else {
          failed.push(item);
          failedIndexes.push(index);
        }
      } catch {
        failed.push(item);
        failedIndexes.push(index);
      }
    }
    return { created, failed, failedIndexes, structurePartnersCreated: createdProviderIds.size };
  };

  return { createSocialOffers, loading };
};
